import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../../helpers/api';
import { splitQualiaTaskError, toastOrderUpdateResult } from '../../../helpers/qualia';
import { ENDPOINTS } from '../../../helpers/constants';
import AppModal from '../../common/AppModal';
import EstoppelOrderForm from './Form/EstoppelOrderForm';
import LienOrderForm from './Form/LienOrderForm';
import SurveyOrderForm from './Form/SurveyOrderForm';
import {
  appendHoaSlotFiles,
  hasPendingHoaFiles,
  serializeClientRequestHoaInfo,
  serializeHomeOwnerInformation,
} from '../../hoaForm/hoaFormData';

const createObjectWithoutEmptyValues = inputObj => {
  const newObj = {};

  for (const key in inputObj) {
    if (
      inputObj.hasOwnProperty(key) &&
      ((inputObj[key] !== null && inputObj[key] !== undefined) || inputObj[key]?.length > 0)
    ) {
      if (key === 'homeOwnerInformation') {
        const hoa = [];
        inputObj[key].forEach(h => {
          const hoaObject = {};
          for (const objKey in h) {
            if (h.hasOwnProperty(objKey) && h[objKey] !== '') {
              hoaObject[objKey] = h[objKey];
            }
          }
          hoa.push(hoaObject);
        });
        newObj[key] = hoa;
        continue;
      }
      if (key === 'requestHoaInfo') {
        newObj[key] = inputObj[key];
        continue;
      }
      newObj[key] = inputObj[key];
    }
  }

  return newObj;
};

const AddEditOrderSidebar = ({
  isPublic,
  show,
  editingOrder,
  onHide,
  onOrderAddUpdate,
  orderType,
  service,
  serviceCreation = false,
  /** When set (Estoppel), only that HOA slot is editable in the form. */
  hoaEditIndex = null,
  /** Skip API persist — return form values to parent as a local draft. */
  draftOnly = false,
  /** Report Builder HOA edit only — allow setting an association's directory preferred contact. */
  enablePreferredContactsForHoaEdit = false
}) => {
  const [loading, setLoading] = useState(false);
  // Hide Update HOA while nested Add/Edit Association (or mgmt company) modal is open.
  const [nestedDirectoryModalOpen, setNestedDirectoryModalOpen] = useState(false);

  useEffect(() => {
    if (!show) setNestedDirectoryModalOpen(false);
  }, [show]);

  const onSubmitOrderForm = async (value, { close = true } = {}) => {
    const formData = createObjectWithoutEmptyValues({ ...value });
    formData['orderType'] = orderType;
    formData['service'] = service?._id;
    if (serviceCreation) {
      onOrderAddUpdate({
        ...editingOrder,
        ...formData,
        orderType,
        _id: editingOrder?._id ?? Math.random(),
        homeOwnerInformation: value.homeOwnerInformation ?? formData.homeOwnerInformation,
      });
      if (close) onHide();
      return;
    }
    if (draftOnly) {
      onOrderAddUpdate({
        ...editingOrder,
        ...value,
        homeOwnerInformation: value.homeOwnerInformation
      });
      if (close) onHide();
      return;
    }
    setLoading(true);
    try {
      const hasPendingFiles = hasPendingHoaFiles(formData.homeOwnerInformation);

      let requestBody = formData;
      let contentType = 'application/json';
      let stringify = true;

      if (hasPendingFiles) {
        const multipartFormData = new FormData();
        appendHoaSlotFiles(multipartFormData, formData.homeOwnerInformation);

        const { homeOwnerInformation, ...rest } = formData;
        multipartFormData.append(
          'data',
          JSON.stringify({
            ...rest,
            homeOwnerInformation: serializeHomeOwnerInformation(homeOwnerInformation),
          })
        );

        requestBody = multipartFormData;
        contentType = 'multipart/form-data';
        stringify = false;
      } else if (Array.isArray(formData.requestHoaInfo)) {
        requestBody = formData;
      } else if (Array.isArray(formData.homeOwnerInformation)) {
        requestBody = {
          ...formData,
          homeOwnerInformation: serializeHomeOwnerInformation(formData.homeOwnerInformation),
        };
      }

      const { response, error } = await makeRESTApiRequest({
        endpoint: editingOrder?._id ? ENDPOINTS.ORDER_WITH_ID(editingOrder._id) : ENDPOINTS.CREATE_ORDER,
        method: editingOrder?._id ? 'PUT' : 'POST',
        requestBody,
        contentType,
        stringify
      });
      if (error) {
        toast.error(error);
        return;
      }
      const { order: orderResponse, qualiaTaskError } = splitQualiaTaskError(response);
      toastOrderUpdateResult(
        qualiaTaskError,
        close ? (editingOrder?._id ? 'Successfully Updated' : 'Successfully Added') : ''
      );
      onOrderAddUpdate(orderResponse, { close });
      if (close) onHide();
    } catch (e) {
      toast.error(e);
    } finally {
      setLoading(false);
    }
  };

  const form = () => {
    switch (orderType) {
      case 'Lien Search':
        return (
          <LienOrderForm
            isPublic={isPublic}
            loading={loading}
            serviceCreation={serviceCreation}
            isEditingOrder={{
              ...editingOrder,
              processingFee: editingOrder?.processingFee ?? service?.organization?.lienSearchProcessingFee
            }}
            county={service?.county}
            onSubmit={onSubmitOrderForm}
          />
        );
      case 'Survey':
        return (
          <SurveyOrderForm
            isPublic={isPublic}
            serviceCreation={serviceCreation}
            loading={loading}
            folderId={service?.driveFolder}
            isEditingOrder={{
              ...editingOrder,
              processingFee: editingOrder?.processingFee ?? service?.organization?.surveyProcessingFee
            }}
            onSubmit={onSubmitOrderForm}
          />
        );
      case 'Estoppel':
        return (
          <EstoppelOrderForm
            isPublic={isPublic}
            serviceCreation={serviceCreation}
            loading={loading}
            folderId={service?.driveFolder}
            hoaEditIndex={hoaEditIndex}
            draftOnly={draftOnly}
            enablePreferredContactsForHoaEdit={enablePreferredContactsForHoaEdit}
            onNestedDirectoryModalChange={setNestedDirectoryModalOpen}
            isEditingOrder={{
              ...editingOrder,
              processingFee: editingOrder?.processingFee ?? service?.organization?.estoppelProcessingFee
            }}
            onSubmit={onSubmitOrderForm}
          />
        );
      default:
        return (
          <LienOrderForm
            isPublic={isPublic}
            serviceCreation={serviceCreation}
            loading={loading}
            isEditingOrder={{ ...editingOrder, processingFee: service?.organization?.lienSearchProcessingFees }}
            county={service?.county}
            onSubmit={onSubmitOrderForm}
          />
        );
    }
  };
  return (
    <AppModal
      show={show}
      visuallyHidden={nestedDirectoryModalOpen}
      onHide={onHide}
      size="lg"
      showFooter={false}
      heading={`${!editingOrder?._id ? `Add New ${orderType}` : `Update ${orderType === 'Estoppel' ? 'HOA' : orderType}`}`}
    >
      {show && <div>{form()}</div>}
    </AppModal>
  );
};

export default AddEditOrderSidebar;
